import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faTrash as faTrashSolid } from '@fortawesome/pro-solid-svg-icons';
import { useMutation } from '@tanstack/react-query';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import {
    deleteCorporationAssignmentErrorMessage,
    deleteLocationAssignmentErrorMessage,
} from 'pages/Settings/Users/SingleUserPage';
import { UsersTableCellProps } from 'pages/Settings/Users/UsersTable';
import { useState } from 'react';
import { useThemeValues } from 'styles/theme';

export type CorporationIdOrLocationId = Readonly<{
    locationId?: string;
    corporationId?: string;
}>;

export type RemoveUserCellProps = UsersTableCellProps & CorporationIdOrLocationId;

export const getResourceTypeAndId = (corpOrLocId: CorporationIdOrLocationId) => {
    if (corpOrLocId.locationId !== undefined) {
        return {
            resourceType: 'location',
            resourceId: corpOrLocId.locationId,
        };
    }

    if (corpOrLocId.corporationId !== undefined) {
        return {
            resourceType: 'corporation',
            resourceId: corpOrLocId.corporationId,
        };
    }

    throw new Error('CorporationId or LocationId required');
};

export function RemoveUserCell({ rowData, onChange, ...rest }: RemoveUserCellProps) {
    const { showToast } = useNotificationContext();
    const { secondaryText, deleteButton } = useThemeValues();
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    const { resourceType, resourceId } = getResourceTypeAndId(rest);
    const { handleRemove, getErrorMessage, resourceTypeDisplayName } = (() => {
        switch (resourceType) {
            case 'location':
                return {
                    handleRemove: () =>
                        sdClient.deleteLocationPermission({
                            userId: rowData.Id,
                            locationId: resourceId,
                        }),
                    getErrorMessage: deleteLocationAssignmentErrorMessage,
                    resourceTypeDisplayName: 'Location',
                };
            case 'corporation':
                return {
                    handleRemove: () =>
                        sdClient.deleteCorporationPermission({
                            userId: rowData.Id,
                            corporationId: resourceId,
                        }),
                    getErrorMessage: deleteCorporationAssignmentErrorMessage,
                    resourceTypeDisplayName: 'Corporation',
                };
            default:
                throw new Error('cosmic rays');
        }
    })();

    const { mutate: removeUserAssignment, isPending: isRemoving } = useMutation({
        mutationFn: handleRemove,

        onSuccess: () => {
            onChange();
            setConfirmModalOpen(false);
            showToast({
                type: 'success',
                content: `Successfully removed user from ${resourceType}.`,
            });
        },

        onError: (e) => {
            showToast({
                type: 'error',
                content: getErrorMessage(e),
            });
        },
    });

    return (
        <>
            <HoverIconButton
                icon={faTrash}
                hoverIcon={faTrashSolid}
                color={secondaryText}
                hoverColor={deleteButton}
                onClick={() => setConfirmModalOpen(true)}
            />
            {confirmModalOpen && (
                <ConfirmModal
                    title={`Remove User From ${resourceTypeDisplayName}`}
                    actionText={`remove this user from this ${resourceType}?`}
                    checkText={`remove this user from this ${resourceType}`}
                    closable={!isRemoving}
                    actionable={!isRemoving}
                    actionButtonText="Remove"
                    onAction={removeUserAssignment}
                    onClose={() => setConfirmModalOpen(false)}
                />
            )}
        </>
    );
}
